import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, LogOut, Calendar, MapPin, Clock, Ticket, Users, X, Check, Mail, Download, Disc3, Music } from "lucide-react";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { RemixesPanel } from "@/components/admin/RemixesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [releases, setReleases] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [activeTab, setActiveTab] = useState("events");
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "", date: "", time: "", venue: "", city: "", ticket_link: "", description: ""
  });
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    title: "", label: "", year: "", genre: "", artwork_url: "", beatport_url: "", spotify_url: "", feat: "", is_new: false, is_hype: false, sort_order: 0
  });
  const [mixDialogOpen, setMixDialogOpen] = useState(false);
  const [editingMix, setEditingMix] = useState(null);
  const [mixForm, setMixForm] = useState({ title: "", soundcloud_url: "", sort_order: 0 });
  const [remixCount, setRemixCount] = useState(0);
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(res.data);
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    }
  }, [token]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscribers`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(res.data);
    } catch (err) {
      console.error("Failed to fetch subscribers:", err);
    }
  }, [token]);

  const fetchReleases = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/releases`);
      setReleases(res.data);
    } catch (err) {
      console.error("Failed to fetch releases:", err);
    }
  }, []);

  const fetchMixes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mixes`);
      setMixes(res.data);
    } catch (err) {
      console.error("Failed to fetch mixes:", err);
    }
  }, []);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) {
      fetchEvents();
      fetchBookings();
      fetchSubscribers();
      fetchReleases();
      fetchMixes();
      axios.get(`${API}/remixes`).then((res) => setRemixCount(res.data.length)).catch(() => {});
    }
  }, [token, fetchEvents, fetchBookings, fetchSubscribers, fetchReleases, fetchMixes]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("admin_token", t);
      toast.success("Logged in successfully");
    } catch (err) {
      console.error("Login failed:", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid password");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("admin_token");
    toast.success("Logged out");
  };

  const exportSubscribersCSV = () => {
    if (subscribers.length === 0) return;
    const header = "Email,Subscribed At\n";
    const rows = subscribers.map((s) => `${s.email},${s.subscribed_at}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "alchemist-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Subscribers exported");
  };

  const handleReleaseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRelease) {
        await axios.put(`${API}/releases/${editingRelease.id}`, releaseForm, { headers: authHeaders });
        toast.success("Release updated");
      } else {
        await axios.post(`${API}/releases`, releaseForm, { headers: authHeaders });
        toast.success("Release added");
      }
      setReleaseDialogOpen(false);
      setEditingRelease(null);
      setReleaseForm({ title: "", label: "", year: "", genre: "", artwork_url: "", beatport_url: "", spotify_url: "", feat: "", is_new: false, is_hype: false, sort_order: 0 });
      fetchReleases();
    } catch (err) {
      console.error("Failed to save release:", err);
      toast.error("Failed to save release");
    }
  };

  const handleDeleteRelease = async (id) => {
    try {
      await axios.delete(`${API}/releases/${id}`, { headers: authHeaders });
      toast.success("Release deleted");
      fetchReleases();
    } catch (err) {
      console.error("Failed to delete release:", err);
      toast.error("Failed to delete release");
    }
  };

  const openEditRelease = (release) => {
    setEditingRelease(release);
    setReleaseForm({
      title: release.title, label: release.label, year: release.year, genre: release.genre,
      artwork_url: release.artwork_url, beatport_url: release.beatport_url, spotify_url: release.spotify_url || "",
      feat: release.feat, is_new: release.is_new, is_hype: release.is_hype, sort_order: release.sort_order,
    });
    setReleaseDialogOpen(true);
  };

  const openNewRelease = () => {
    setEditingRelease(null);
    setReleaseForm({ title: "", label: "", year: "", genre: "", artwork_url: "", beatport_url: "", spotify_url: "", feat: "", is_new: false, is_hype: false, sort_order: releases.length });
    setReleaseDialogOpen(true);
  };

  const handleMixSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMix) {
        await axios.put(`${API}/mixes/${editingMix.id}`, mixForm, { headers: authHeaders });
        toast.success("Mix updated");
      } else {
        await axios.post(`${API}/mixes`, mixForm, { headers: authHeaders });
        toast.success("Mix added");
      }
      setMixDialogOpen(false);
      setEditingMix(null);
      setMixForm({ title: "", soundcloud_url: "", sort_order: 0 });
      fetchMixes();
    } catch (err) {
      console.error("Failed to save mix:", err);
      toast.error("Failed to save mix");
    }
  };

  const handleDeleteMix = async (id) => {
    try {
      await axios.delete(`${API}/mixes/${id}`, { headers: authHeaders });
      toast.success("Mix deleted");
      fetchMixes();
    } catch (err) {
      console.error("Failed to delete mix:", err);
      toast.error("Failed to delete mix");
    }
  };

  const openEditMix = (mix) => {
    setEditingMix(mix);
    setMixForm({ title: mix.title, soundcloud_url: mix.soundcloud_url, sort_order: mix.sort_order });
    setMixDialogOpen(true);
  };

  const openNewMix = () => {
    setEditingMix(null);
    setMixForm({ title: "", soundcloud_url: "", sort_order: mixes.length });
    setMixDialogOpen(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventForm, { headers: authHeaders });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, eventForm, { headers: authHeaders });
        toast.success("Event created");
      }
      setEventDialogOpen(false);
      setEditingEvent(null);
      setEventForm({ title: "", date: "", time: "", venue: "", city: "", ticket_link: "", description: "" });
      fetchEvents();
    } catch (err) {
      console.error("Failed to save event:", err);
      toast.error("Failed to save event");
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { headers: authHeaders });
      toast.success("Event deleted");
      fetchEvents();
    } catch (err) {
      console.error("Failed to delete event:", err);
      toast.error("Failed to delete event");
    }
  };

  const handleDeleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: authHeaders });
      toast.success("Booking deleted");
      fetchBookings();
    } catch (err) {
      console.error("Failed to delete booking:", err);
      toast.error("Failed to delete booking");
    }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title, date: event.date, time: event.time,
      venue: event.venue, city: event.city, ticket_link: event.ticket_link, description: event.description,
    });
    setEventDialogOpen(true);
  };

  const openNewEvent = () => {
    setEditingEvent(null);
    setEventForm({ title: "", date: "", time: "", venue: "", city: "", ticket_link: "", description: "" });
    setEventDialogOpen(true);
  };

  // Login Screen
  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="mb-12">
            <h1 className="mb-2">
              <img src="/images/alchemist-logo.png" alt="Alchemist" className="h-8 w-auto" />
            </h1>
            <p className="font-sub text-xs text-white/40 tracking-widest">ADMIN PANEL</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-8">
            <div>
              <Label className="font-sub text-xs text-white/50 tracking-widest mb-3 block">PASSWORD</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/20 focus-visible:ring-0 focus-visible:border-white"
                placeholder="Enter admin password"
                required
              />
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="btn-alchemist w-full border border-white/20 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300"
            >
              Access Panel
            </button>
          </form>
          <button
            onClick={() => navigate("/")}
            className="mt-8 font-sub text-xs text-white/30 hover:text-white tracking-widest transition-colors duration-300"
          >
            Back to Site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      {/* Admin Header */}
      <header className="border-b border-white/10 px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <img src="/images/alchemist-logo.png" alt="Alchemist" className="h-6 w-auto" />
          <span className="font-sub text-[10px] text-white/30 tracking-widest">ADMIN</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="font-sub text-xs text-white/40 hover:text-white tracking-widest transition-colors duration-300"
          >
            View Site
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 font-sub text-xs text-white/40 hover:text-white tracking-widest transition-colors duration-300"
          >
            <LogOut className="w-3 h-3" />
            Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-8 mb-12 border-b border-white/10">
          <button
            data-testid="events-tab"
            onClick={() => setActiveTab("events")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "events" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Calendar className="w-3 h-3 inline mr-2" />
            Events ({events.length})
          </button>
          <button
            data-testid="bookings-tab"
            onClick={() => setActiveTab("bookings")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "bookings" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Users className="w-3 h-3 inline mr-2" />
            Bookings ({bookings.length})
          </button>
          <button
            data-testid="subscribers-tab"
            onClick={() => setActiveTab("subscribers")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "subscribers" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Mail className="w-3 h-3 inline mr-2" />
            Subscribers ({subscribers.length})
          </button>
          <button
            data-testid="releases-tab"
            onClick={() => setActiveTab("releases")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "releases" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Disc3 className="w-3 h-3 inline mr-2" />
            Releases ({releases.length})
          </button>
          <button
            data-testid="mixes-tab"
            onClick={() => setActiveTab("mixes")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "mixes" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Music className="w-3 h-3 inline mr-2" />
            Mixes ({mixes.length})
          </button>
          <button
            data-testid="remixes-tab"
            onClick={() => setActiveTab("remixes")}
            className={`font-sub text-xs tracking-widest pb-4 transition-colors duration-300 ${
              activeTab === "remixes" ? "text-white border-b border-white" : "text-white/30 hover:text-white/60"
            }`}
          >
            <Download className="w-3 h-3 inline mr-2" />
            Remixes ({remixCount})
          </button>
        </div>

        {activeTab === "remixes" && <RemixesPanel token={token} onCountChange={setRemixCount} />}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div data-testid="events-panel">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl tracking-tighter">Upcoming Events</h2>
              <button
                data-testid="add-event-btn"
                onClick={openNewEvent}
                className="btn-alchemist flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300"
              >
                <Plus className="w-3 h-3" />
                Add Event
              </button>
            </div>

            {events.length === 0 ? (
              <div className="text-center py-24 text-white/20">
                <Calendar className="w-8 h-8 mx-auto mb-4 opacity-30" />
                <p className="font-sub text-xs tracking-widest">No events yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">DATE</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">TITLE</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">VENUE</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">CITY</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40 text-right">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id} data-testid={`event-row-${event.id}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-sub text-xs text-white/60">{event.date}</TableCell>
                      <TableCell className="text-white font-medium">{event.title}</TableCell>
                      <TableCell className="text-white/60">{event.venue}</TableCell>
                      <TableCell className="text-white/60">{event.city}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="font-sub text-xs text-white/30 hover:text-white tracking-widest transition-colors duration-300 px-2 py-1">
                            ...
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#0f0f0f] border-white/10">
                            <DropdownMenuItem
                              data-testid={`edit-event-${event.id}`}
                              onClick={() => openEditEvent(event)}
                              className="text-white/70 hover:text-white cursor-pointer"
                            >
                              <Edit2 className="w-3 h-3 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem
                              data-testid={`delete-event-${event.id}`}
                              onClick={() => handleDeleteEvent(event.id)}
                              className="text-red-400 hover:text-red-300 cursor-pointer"
                            >
                              <Trash2 className="w-3 h-3 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div data-testid="bookings-panel">
            <h2 className="font-heading text-2xl tracking-tighter mb-8">Booking Requests</h2>
            {bookings.length === 0 ? (
              <div className="text-center py-24 text-white/20">
                <Users className="w-8 h-8 mx-auto mb-4 opacity-30" />
                <p className="font-sub text-xs tracking-widest">No bookings yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    data-testid={`booking-card-${booking.id}`}
                    className="border border-white/10 p-6 hover:border-white/20 transition-colors duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-3">
                        <div className="flex items-center gap-4">
                          <span className="text-white font-medium">{booking.name}</span>
                          <span className="font-sub text-[10px] text-white/30 tracking-widest px-2 py-1 border border-white/10">
                            {booking.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-white/40 text-sm">{booking.email}</p>
                        {booking.event_type && (
                          <p className="font-sub text-xs text-white/50">
                            <span className="text-white/30">Type:</span> {booking.event_type}
                          </p>
                        )}
                        {booking.preferred_date && (
                          <p className="font-sub text-xs text-white/50">
                            <span className="text-white/30">Date:</span> {booking.preferred_date}
                          </p>
                        )}
                        {booking.message && (
                          <p className="text-white/50 text-sm mt-2">{booking.message}</p>
                        )}
                      </div>
                      <button
                        data-testid={`delete-booking-${booking.id}`}
                        onClick={() => handleDeleteBooking(booking.id)}
                        className="text-white/20 hover:text-red-400 transition-colors duration-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <div data-testid="subscribers-panel">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl tracking-tighter">Mailing List</h2>
              <button
                data-testid="export-subscribers-btn"
                onClick={exportSubscribersCSV}
                disabled={subscribers.length === 0}
                className="btn-alchemist flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <Download className="w-3 h-3" />
                Export CSV
              </button>
            </div>
            {subscribers.length === 0 ? (
              <div className="text-center py-24 text-white/20">
                <Mail className="w-8 h-8 mx-auto mb-4 opacity-30" />
                <p className="font-sub text-xs tracking-widest">No subscribers yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">#</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">EMAIL</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">SUBSCRIBED</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((sub, i) => (
                    <TableRow key={sub.id} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-sub text-xs text-white/30">{i + 1}</TableCell>
                      <TableCell className="text-white">{sub.email}</TableCell>
                      <TableCell className="font-sub text-xs text-white/40">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {/* Releases Tab */}
        {activeTab === "releases" && (
          <div data-testid="releases-panel">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl tracking-tighter">Releases</h2>
              <button
                data-testid="add-release-btn"
                onClick={openNewRelease}
                className="btn-alchemist flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300"
              >
                <Plus className="w-3 h-3" />
                Add Release
              </button>
            </div>
            {releases.length === 0 ? (
              <div className="text-center py-24 text-white/20">
                <Disc3 className="w-8 h-8 mx-auto mb-4 opacity-30" />
                <p className="font-sub text-xs tracking-widest">No releases yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {releases.map((release) => (
                  <div key={release.id} data-testid={`admin-release-${release.id}`} className="group relative">
                    <div className="relative aspect-square overflow-hidden border border-white/10 mb-3">
                      {release.artwork_url ? (
                        <img src={release.artwork_url} alt={release.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
                          <Disc3 className="w-8 h-8 text-white/10" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-[#050505]/40" />
                      <div className="absolute top-2 left-2 flex gap-1.5">
                        {release.is_new && <span className="font-sub text-[8px] tracking-widest bg-white text-black px-2 py-0.5">NEW</span>}
                        {release.is_hype && <span className="font-sub text-[8px] tracking-widest bg-white/10 border border-white/20 text-white px-2 py-0.5">HYPE</span>}
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button
                          onClick={() => openEditRelease(release)}
                          className="w-7 h-7 bg-[#050505]/80 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                        >
                          <Edit2 className="w-3 h-3 text-white/70" />
                        </button>
                        <button
                          onClick={() => handleDeleteRelease(release.id)}
                          className="w-7 h-7 bg-[#050505]/80 border border-white/20 flex items-center justify-center hover:bg-red-500/30 transition-colors duration-300"
                        >
                          <Trash2 className="w-3 h-3 text-white/70" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm tracking-tight text-white truncate">{release.title}</h3>
                    {release.feat && <p className="font-body text-[11px] text-white/30 truncate">feat. {release.feat}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-sub text-[9px] text-white/25 tracking-widest">{(release.label || "").toUpperCase()}</span>
                      <span className="text-white/10">|</span>
                      <span className="font-sub text-[9px] text-white/20 tracking-widest">{release.year}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mixes Tab */}
        {activeTab === "mixes" && (
          <div data-testid="mixes-panel">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl tracking-tighter">SoundCloud Mixes</h2>
              <button
                data-testid="add-mix-btn"
                onClick={openNewMix}
                className="btn-alchemist flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300"
              >
                <Plus className="w-3 h-3" />
                Add Mix
              </button>
            </div>
            {mixes.length === 0 ? (
              <div className="text-center py-24 text-white/20">
                <Music className="w-8 h-8 mx-auto mb-4 opacity-30" />
                <p className="font-sub text-xs tracking-widest">No mixes yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">ORDER</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">TITLE</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40">SOUNDCLOUD URL</TableHead>
                    <TableHead className="font-sub text-[10px] tracking-widest text-white/40 text-right">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mixes.map((mix) => (
                    <TableRow key={mix.id} data-testid={`mix-row-${mix.id}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-sub text-xs text-white/40">{mix.sort_order}</TableCell>
                      <TableCell className="text-white font-medium">{mix.title}</TableCell>
                      <TableCell className="text-white/40 font-sub text-xs truncate max-w-md">
                        <a href={mix.soundcloud_url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                          {mix.soundcloud_url}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="font-sub text-xs text-white/30 hover:text-white tracking-widest transition-colors duration-300 px-2 py-1">
                            ...
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#0f0f0f] border-white/10">
                            <DropdownMenuItem
                              data-testid={`edit-mix-${mix.id}`}
                              onClick={() => openEditMix(mix)}
                              className="text-white/70 hover:text-white cursor-pointer"
                            >
                              <Edit2 className="w-3 h-3 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem
                              data-testid={`delete-mix-${mix.id}`}
                              onClick={() => handleDeleteMix(mix.id)}
                              className="text-red-400 hover:text-red-300 cursor-pointer"
                            >
                              <Trash2 className="w-3 h-3 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>

      {/* Event Form Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tighter">
              {editingEvent ? "EDIT EVENT" : "NEW EVENT"}
            </DialogTitle>
            <DialogDescription className="font-sub text-xs text-white/40 tracking-widest">
              {editingEvent ? "Update event details" : "Add a new upcoming event"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEventSubmit} className="space-y-5 mt-4">
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TITLE</Label>
              <Input
                data-testid="event-title-input"
                value={eventForm.title}
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">DATE</Label>
                <Input
                  data-testid="event-date-input"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white [color-scheme:dark]"
                  required
                />
              </div>
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TIME</Label>
                <Input
                  data-testid="event-time-input"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  placeholder="e.g. 22:00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">VENUE</Label>
                <Input
                  data-testid="event-venue-input"
                  value={eventForm.venue}
                  onChange={(e) => setEventForm({...eventForm, venue: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  required
                />
              </div>
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">CITY</Label>
                <Input
                  data-testid="event-city-input"
                  value={eventForm.city}
                  onChange={(e) => setEventForm({...eventForm, city: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TICKET LINK</Label>
              <Input
                data-testid="event-ticket-input"
                value={eventForm.ticket_link}
                onChange={(e) => setEventForm({...eventForm, ticket_link: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="https://"
              />
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">DESCRIPTION</Label>
              <Textarea
                data-testid="event-description-input"
                value={eventForm.description}
                onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                className="bg-transparent border border-white/10 rounded-none px-3 py-3 text-white focus-visible:ring-0 focus-visible:border-white/30 min-h-[80px]"
              />
            </div>
            <button
              data-testid="event-submit-btn"
              type="submit"
              className="btn-alchemist w-full border border-white/20 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300 mt-2"
            >
              {editingEvent ? "Update Event" : "Create Event"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Release Form Dialog */}
      <Dialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tighter">
              {editingRelease ? "EDIT RELEASE" : "NEW RELEASE"}
            </DialogTitle>
            <DialogDescription className="font-sub text-xs text-white/40 tracking-widest">
              {editingRelease ? "Update release details" : "Add a new release"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReleaseSubmit} className="space-y-5 mt-4">
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TITLE</Label>
              <Input
                data-testid="release-title-input"
                value={releaseForm.title}
                onChange={(e) => setReleaseForm({...releaseForm, title: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="e.g. Anand Dhara / Love"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">LABEL</Label>
                <Input
                  value={releaseForm.label}
                  onChange={(e) => setReleaseForm({...releaseForm, label: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  placeholder="e.g. Interplay Soul"
                />
              </div>
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">YEAR</Label>
                <Input
                  value={releaseForm.year}
                  onChange={(e) => setReleaseForm({...releaseForm, year: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  placeholder="2025"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">GENRE</Label>
                <Input
                  value={releaseForm.genre}
                  onChange={(e) => setReleaseForm({...releaseForm, genre: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  placeholder="Melodic House & Techno"
                />
              </div>
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">FEAT.</Label>
                <Input
                  value={releaseForm.feat}
                  onChange={(e) => setReleaseForm({...releaseForm, feat: e.target.value})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                  placeholder="Artist name"
                />
              </div>
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">ARTWORK URL</Label>
              <Input
                value={releaseForm.artwork_url}
                onChange={(e) => setReleaseForm({...releaseForm, artwork_url: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">BEATPORT URL</Label>
              <Input
                value={releaseForm.beatport_url}
                onChange={(e) => setReleaseForm({...releaseForm, beatport_url: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="https://www.beatport.com/..."
              />
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">SPOTIFY URL</Label>
              <Input
                value={releaseForm.spotify_url}
                onChange={(e) => setReleaseForm({...releaseForm, spotify_url: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="https://open.spotify.com/..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReleaseForm({...releaseForm, is_new: !releaseForm.is_new})}
                  className={`w-8 h-8 border flex items-center justify-center transition-colors duration-300 ${
                    releaseForm.is_new ? "border-white bg-white/10" : "border-white/20"
                  }`}
                >
                  {releaseForm.is_new && <Check className="w-3 h-3 text-white" />}
                </button>
                <Label className="font-sub text-[10px] text-white/50 tracking-widest">NEW</Label>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReleaseForm({...releaseForm, is_hype: !releaseForm.is_hype})}
                  className={`w-8 h-8 border flex items-center justify-center transition-colors duration-300 ${
                    releaseForm.is_hype ? "border-white bg-white/10" : "border-white/20"
                  }`}
                >
                  {releaseForm.is_hype && <Check className="w-3 h-3 text-white" />}
                </button>
                <Label className="font-sub text-[10px] text-white/50 tracking-widest">HYPE</Label>
              </div>
              <div>
                <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">ORDER</Label>
                <Input
                  type="number"
                  value={releaseForm.sort_order}
                  onChange={(e) => setReleaseForm({...releaseForm, sort_order: parseInt(e.target.value) || 0})}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white w-full"
                />
              </div>
            </div>
            <button
              data-testid="release-submit-btn"
              type="submit"
              className="btn-alchemist w-full border border-white/20 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300 mt-2"
            >
              {editingRelease ? "Update Release" : "Add Release"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mix Form Dialog */}
      <Dialog open={mixDialogOpen} onOpenChange={setMixDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tighter">
              {editingMix ? "EDIT MIX" : "NEW MIX"}
            </DialogTitle>
            <DialogDescription className="font-sub text-xs text-white/40 tracking-widest">
              {editingMix ? "Update SoundCloud mix details" : "Add a new SoundCloud set"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMixSubmit} className="space-y-5 mt-4">
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TITLE</Label>
              <Input
                data-testid="mix-title-input"
                value={mixForm.title}
                onChange={(e) => setMixForm({...mixForm, title: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="e.g. Support Set for Eelke Kleijn — The Story, Toronto"
                required
              />
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">SOUNDCLOUD URL</Label>
              <Input
                data-testid="mix-url-input"
                value={mixForm.soundcloud_url}
                onChange={(e) => setMixForm({...mixForm, soundcloud_url: e.target.value})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
                placeholder="https://soundcloud.com/your-name/track-slug"
                required
              />
              <p className="font-sub text-[10px] text-white/30 mt-2 tracking-wide">
                Paste the full SoundCloud track URL (not the share short link).
              </p>
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">SORT ORDER</Label>
              <Input
                data-testid="mix-order-input"
                type="number"
                value={mixForm.sort_order}
                onChange={(e) => setMixForm({...mixForm, sort_order: parseInt(e.target.value) || 0})}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white"
              />
              <p className="font-sub text-[10px] text-white/30 mt-2 tracking-wide">
                Lower numbers appear first. 0 = top of the list.
              </p>
            </div>
            <button
              data-testid="mix-submit-btn"
              type="submit"
              className="btn-alchemist w-full border border-white/20 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300 mt-2"
            >
              {editingMix ? "Update Mix" : "Add Mix"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
